import {
  DEFAULT_FOUNDING_AGENT_SERVICE_URL,
  normalizeAppearance,
  normalizeIntakeForm,
  normalizeTeaser,
} from "@moss-js/founding-agent/core";

// Local mirrors of core types. Defined here (not imported from
// @moss-js/founding-agent/core) so the embed's PUBLIC types are self-contained:
// dist/moss.d.ts must not reference @moss-js/founding-agent, which is a
// build-time devDependency absent when consumers npm-install just the embed.
// (Runtime imports like normalizeAppearance are fine; they get bundled.)
export type BubbleColor =
  | "violet"
  | "cobalt"
  | "teal"
  | "emerald"
  | "coral"
  | "amber"
  | `#${string}`;

/** Portal-configured widget appearance (mirror of the core contract). */
export interface AgentAppearance {
  shape: "pixel" | "flow" | "radio" | "stripes" | "conic";
  /** 1-2 hex colors. */
  colors: string[];
  /** Post-blur in px, 0-24, authored against a 320px stage. */
  soften: number;
  /** Grain overlay strength, 0-1. */
  noise: number;
  widget_theme: "light" | "dark";
}

/** Portal-configured intake-form field (mirror of the core contract). */
export interface IntakeField {
  key: string;
  label: string;
  type: "text" | "email" | "phone";
  required: boolean;
}

/** Portal-configured intake-form config (mirror of the core contract). */
export interface IntakeFormConfig {
  enabled: boolean;
  trigger: { after_answers: number };
  title?: string;
  fields: IntakeField[];
}

/** Portal-configured teaser (mirror of the core contract). */
export interface TeaserConfig {
  enabled: boolean;
  text: string;
}

export type ThemeMode = "light" | "dark";
export type Position = "fixed" | "inline";
export type Side = "left" | "center" | "right";

export interface WidgetConfig {
  publishableKey: string;
  serviceUrl: string;
  id?: string;
  position: Position;
  target?: string;
  /** Where along the bottom edge the fixed bubble sits. Ignored inline. */
  side: Side;
  /** Pixel gap from the side edge (not used when centered). Unset = the stylesheet's (24px or page CSS). */
  offsetX?: number;
  /** Pixel gap from the bottom edge. Unset = the stylesheet's (24px or page CSS). */
  offsetY?: number;
  /** Lift the fixed bubble above visible matches that would cover it. */
  avoidSelector?: string;
  color: BubbleColor;
  /**
   * Explicitly requested theme. `undefined` = not supplied, so a fetched
   * appearance's `widget_theme` may fill it (explicit always wins).
   */
  themeMode?: ThemeMode;
  /**
   * Explicit appearance override. Wins over the fetched portal config.
   * Normalized (clamped, valid hex) at resolve time.
   */
  appearance?: AgentAppearance;
  label?: string;
  hideOnSelector?: string;
  onNavigate?: (path: string) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (err: Error) => void;
  onTranscript?: (text: string) => void;
  onBookingComplete?: () => void;
  onHandoff?: () => void;
}

export interface PublicConfig {
  name: string;
  description: string;
  greeting: string;
  suggested_prompts?: string[];
  agent_mode: "static" | "interactive";
  /** Portal-configured appearance; `null` = never configured. */
  appearance: AgentAppearance | null;
  /** Portal-configured intake form; absent/`null`/malformed = not configured. */
  intake_form?: IntakeFormConfig | null;
  /** Portal-configured teaser above the idle bubble; `null` unless enabled and valid. */
  teaser?: TeaserConfig | null;
}

/** Strongly-typed public interface for the init() API. */
export interface WidgetInput {
  publishableKey: string;
  serviceUrl?: string;
  id?: string;
  position?: Position;
  target?: string;
  side?: Side;
  offsetX?: number;
  offsetY?: number;
  avoidSelector?: string;
  color?: BubbleColor;
  themeMode?: ThemeMode;
  appearance?: AgentAppearance;
  label?: string;
  hideOnSelector?: string;
  onNavigate?: (path: string) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (err: Error) => void;
  onTranscript?: (text: string) => void;
  onBookingComplete?: () => void;
  onHandoff?: () => void;
}

/** Loose shape for the untrusted queue/data-attribute path. */
export type RawWidgetInput = Record<string, unknown>;

/** Init options from an element's data-moss-* attributes (auto-init). */
export function inputFromDataset(ds: DOMStringMap): RawWidgetInput {
  return {
    publishableKey: ds.mossPk,
    position: ds.mossPosition,
    target: ds.mossTarget,
    color: ds.mossColor,
    hideOnSelector: ds.mossHideOn,
    id: ds.mossId,
    side: ds.mossSide,
    offsetX: ds.mossOffsetX,
    offsetY: ds.mossOffsetY,
    avoidSelector: ds.mossAvoid,
  };
}

// A non-negative pixel count: a number, or a data-attribute string like "40"
// or "40px". Anything else warns and falls back to the stylesheet's offset.
function toPixels(value: unknown, option: string): number | undefined {
  if (value === undefined) return undefined;
  const m = typeof value === "string" ? /^\s*(\d+(?:\.\d+)?)(?:px)?\s*$/.exec(value) : null;
  const px = typeof value === "number" ? value : m ? Number(m[1]) : Number.NaN;
  if (Number.isFinite(px) && px >= 0) return px;
  console.warn(`MossFoundingAgent: invalid ${option} "${String(value)}"; expected pixels, using the default`);
  return undefined;
}

export function resolveConfig(input: WidgetInput | RawWidgetInput): WidgetConfig {
  const pk = input.publishableKey;
  if (typeof pk !== "string" || !pk) {
    throw new Error("MossFoundingAgent: publishableKey is required");
  }

  let base = typeof input.serviceUrl === "string" ? input.serviceUrl : DEFAULT_FOUNDING_AGENT_SERVICE_URL;
  while (base.endsWith("/")) base = base.slice(0, -1);

  return {
    publishableKey: pk,
    serviceUrl: base,
    id: typeof input.id === "string" && input.id ? input.id : undefined,
    position: (input.position === "inline" ? "inline" : "fixed") as Position,
    target: typeof input.target === "string" ? input.target : undefined,
    side: input.side === "left" || input.side === "center" ? input.side : "right",
    offsetX: toPixels(input.offsetX, "offsetX"),
    offsetY: toPixels(input.offsetY, "offsetY"),
    avoidSelector: typeof input.avoidSelector === "string" ? input.avoidSelector : undefined,
    color: (typeof input.color === "string" ? input.color : "violet") as BubbleColor,
    // Tri-state: undefined = "not supplied", so a fetched appearance's
    // widget_theme can fill it. Only explicit "dark"/"light" pin the theme.
    themeMode:
      input.themeMode === "dark" || input.themeMode === "light"
        ? (input.themeMode as ThemeMode)
        : undefined,
    appearance:
      typeof input.appearance === "object" && input.appearance !== null
        ? (normalizeAppearance(input.appearance) as AgentAppearance)
        : undefined,
    label: typeof input.label === "string" ? input.label : undefined,
    hideOnSelector: typeof input.hideOnSelector === "string" ? input.hideOnSelector : undefined,
    onNavigate: typeof input.onNavigate === "function" ? (input.onNavigate as (path: string) => void) : undefined,
    onConnect: typeof input.onConnect === "function" ? (input.onConnect as () => void) : undefined,
    onDisconnect: typeof input.onDisconnect === "function" ? (input.onDisconnect as () => void) : undefined,
    onError: typeof input.onError === "function" ? (input.onError as (err: Error) => void) : undefined,
    onTranscript: typeof input.onTranscript === "function" ? (input.onTranscript as (text: string) => void) : undefined,
    onBookingComplete: typeof input.onBookingComplete === "function" ? (input.onBookingComplete as () => void) : undefined,
    onHandoff: typeof input.onHandoff === "function" ? (input.onHandoff as () => void) : undefined,
  };
}

export async function fetchPublicConfig(serviceUrl: string, publishableKey: string): Promise<PublicConfig> {
  let base = serviceUrl;
  while (base.endsWith("/")) base = base.slice(0, -1);

  const res = await fetch(`${base}/api/founding-agent/config?key=${encodeURIComponent(publishableKey)}`);
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`Failed to load founding agent config (${res.status}): ${body}`);
  }
  const data = await res.json() as Partial<PublicConfig>;
  return {
    name: data.name ?? "",
    description: data.description ?? "",
    greeting: data.greeting ?? "",
    suggested_prompts: data.suggested_prompts ?? [],
    // Default-deny: only an explicit "interactive" grants browser-driving
    // (navigate/scroll/page-context). A missing or malformed mode stays static
    // so a bad/old config response cannot enable agent-controlled navigation.
    agent_mode: data.agent_mode === "interactive" ? "interactive" : "static",
    // normalizeAppearance never throws; null/absent stays null so the bubble
    // keeps its preset aura for never-configured agents.
    appearance:
      data.appearance == null
        ? null
        : (normalizeAppearance(data.appearance) as AgentAppearance),
    // Fail-closed via the core normalizer (runtime import, bundled): a
    // malformed payload collapses to "not configured" rather than throw later
    // inside noteAgentAnswerEdge(). Absent, null, and malformed all collapse
    // to the same null shape.
    intake_form: normalizeIntakeForm(data.intake_form),
    teaser: normalizeTeaser(data.teaser),
  };
}
